import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { strapiService } from '../../../utils/api';
import AlertService from '../../../utils/alertService';
import {
  Badge, Button, EmptyState, IconButton, SearchInput, Select, Sym, initialsOf,
} from '../../../components/app2';
import CreateEmployeeAccessModal from './CreateEmployeeAccessModal';

/**
 * Colaboradores de la empresa (rediseño jul-2026).
 *
 * El diseño dibuja una lista compacta (nombre · área · último recibo · neto);
 * aquí se conserva la TABLA completa con todas sus columnas ordenables y las
 * tres acciones por fila (ver portal, crear acceso, restablecer contraseña),
 * porque son la operación real del administrador de empresa. Lo que cambia es
 * el lenguaje visual: avatar con iniciales, badges de estado y el molde de
 * tabla del sistema.
 *
 * Se retiraron dos botones que NO hacían nada ("Exportar" y "Agregar primer
 * empleado", ambos sin acción asociada) y unos console.log de depuración que
 * corrían en cada render.
 */

const COLUMNS = [
  { key: 'name', label: 'Empleado' },
  { key: 'rfc', label: 'RFC' },
  { key: 'employeeNumber', label: 'No. empleado' },
  // Columnas con datos REALES. Antes había cuatro ("Última nómina", "Estado
  // recibo", "Fecha descarga", "Último acceso") calculadas sobre campos que el
  // backend nunca envía: salían siempre en "—" y "Pendiente" para todos.
  { key: 'receiptsCount', label: 'Recibos' },
  { key: 'lastReceiptPeriod', label: 'Último recibo' },
];

/* Las ocho columnas deben caber sin scroll en un escritorio de 1280px: el menú
   lateral (250) y los paddings dejan ~964 útiles. El reparto es
   854 (columnas) + 56 (7 separaciones de 8) + 40 (padding de la fila) = 950.
   Cada ancho sale del contenido real: la columna de acciones son tres botones
   de 34, el RFC son 13 caracteres en mono y el número de empleado 14.
   Aun así el contenedor conserva overflow-x por si la ventana es más angosta. */
const GRID = '114px minmax(150px,1.2fr) 110px 116px 84px 112px';
const GRID_GAP = 8;
const GRID_MIN = 780;

// El filtro pregunta algo que el sistema SÍ sabe. Antes ofrecía
// "Descargado / Visto / Pendiente": el sistema nunca ha registrado si alguien
// abrió su recibo, así que todos caían en "Pendiente" y el filtro no servía.
const STATUS_OPTIONS = [
  { value: 'all', label: 'Todos los colaboradores' },
  { value: 'con', label: 'Con recibos' },
  { value: 'sin', label: 'Sin recibos' },
];

const EmployeeDataTable = ({ companyData }) => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [createAccessModal, setCreateAccessModal] = useState({ isOpen: false, employee: null });
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const employeesData = useMemo(() => {
    if (!companyData?.employees || companyData?.employees?.length === 0) return [];

    return companyData?.employees?.map(employee => ({
      id: employee?.id || employee?.employee_id,
      name: `${employee?.first_name || ''} ${employee?.last_name || ''}`?.trim() ||
            employee?.full_name || employee?.name || 'Nombre no disponible',
      rfc: employee?.rfc || 'No especificado',
      employeeNumber: employee?.employee_number || 'No especificado',
      email: employee?.email || 'Email no disponible',
      userId: employee?.user_id || employee?.userId || null,
      receiptsCount: employee?.receipts_count ?? 0,
      lastReceiptPeriod: employee?.last_receipt_period || null,
      salary: employee?.salary || employee?.monthly_salary || 0,
      department: employee?.department || 'No especificado',
      phone: employee?.phone || employee?.phone_number || null,
      position: employee?.position || employee?.job_title || 'Puesto no especificado',
      isActive: employee?.is_active !== false,
    })) || [];
  }, [companyData?.employees]);

  const filteredAndSortedData = useMemo(() => {
    const filtered = employeesData?.filter(employee => {
      const q = searchTerm?.toLowerCase();
      const matchesSearch =
        employee?.name?.toLowerCase()?.includes(q) ||
        employee?.rfc?.toLowerCase()?.includes(q) ||
        employee?.employeeNumber?.toString()?.toLowerCase()?.includes(q) ||
        employee?.email?.toLowerCase()?.includes(q);
      const matchesStatus = statusFilter === 'all'
        || (statusFilter === 'con' && (employee?.receiptsCount || 0) > 0)
        || (statusFilter === 'sin' && !(employee?.receiptsCount > 0));
      return matchesSearch && matchesStatus;
    });

    if (sortConfig?.key) {
      filtered?.sort((a, b) => {
        let aValue = a?.[sortConfig?.key];
        let bValue = b?.[sortConfig?.key];

        if (['lastReceiptPeriod'].includes(sortConfig?.key)) {
          aValue = new Date(aValue || 0);
          bValue = new Date(bValue || 0);
        }

        if (aValue < bValue) return sortConfig?.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig?.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return filtered;
  }, [employeesData, searchTerm, statusFilter, sortConfig]);

  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredAndSortedData?.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredAndSortedData, currentPage]);

  const totalPages = Math.ceil((filteredAndSortedData?.length || 0) / itemsPerPage);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev?.key === key && prev?.direction === 'asc' ? 'desc' : 'asc',
    }));
    setCurrentPage(1);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString)?.toLocaleDateString('es-MX', {
      day: '2-digit', month: '2-digit', year: 'numeric',
    });
  };

  const goToPayrollProcessing = () => {
    navigate('/app/payroll-processing', {
      state: {
        companyId: companyData?.id || companyData?.company_id,
        companyName: companyData?.company_name || companyData?.businessName,
        companyRfc: companyData?.rfc,
      },
    });
  };

  const handleViewEmployee = (employee) => {
    const companyId = companyData?.id || companyData?.company_id;
    navigate(`/app/employee-payroll-portal?companyId=${companyId}&employeeId=${employee?.id}&from=company`, {
      state: {
        employeeId: employee?.id,
        employeeName: employee?.name,
        employeeEmail: employee?.email,
        employeePosition: employee?.position,
        companyData,
      },
    });
  };

  const handleCreateAccess = (employee) => setCreateAccessModal({ isOpen: true, employee });
  const handleCloseCreateAccessModal = () => setCreateAccessModal({ isOpen: false, employee: null });

  const handleResetPassword = async (employee) => {
    try {
      const employeeId = employee?.id;

      if (!employeeId) {
        await AlertService?.error(
          'Empleado no encontrado',
          'No se pudo identificar al empleado. Por favor intente nuevamente.'
        );
        return;
      }

      const confirmed = await AlertService?.confirm(
        '¿Restablecer contraseña?',
        `Se generará una nueva contraseña temporal para ${employee?.name} y se enviará a su correo electrónico (${employee?.email}). El usuario deberá cambiarla en su próximo inicio de sesión.`
      );

      if (!confirmed) return;

      AlertService?.loading('Restableciendo contraseña...', 'Por favor espere.');

      const response = await strapiService?.users?.resetPassword({ employeeId });

      AlertService?.close();

      await AlertService?.success(
        'Contraseña restablecida',
        `Se ha enviado una contraseña temporal al correo: ${response?.data?.email || employee?.email}. El usuario deberá cambiarla en su próximo inicio de sesión.`
      );

    } catch (error) {
      console.error('❌ Error resetting password:', error);

      AlertService?.close();

      if (error?.response?.status === 403) {
        await AlertService?.error('Sin permisos', 'No tiene permisos para restablecer la contraseña de este usuario.');
      } else if (error?.response?.status === 404) {
        await AlertService?.error('Usuario no encontrado', 'El usuario no existe o fue eliminado.');
      } else if (error?.response?.status === 400) {
        await AlertService?.error('Error al enviar email', 'No se pudo enviar el correo con la contraseña temporal. Por favor intente nuevamente.');
      } else {
        await AlertService?.error(
          'Error al restablecer contraseña',
          error?.response?.data?.error?.message || 'Ocurrió un error al restablecer la contraseña. Por favor intente nuevamente.'
        );
      }
    }
  };

  const sortIcon = (key) => {
    if (sortConfig?.key !== key) return 'unfold_more';
    return sortConfig?.direction === 'asc' ? 'arrow_upward' : 'arrow_downward';
  };

  /** Las tres acciones por fila (mismas en escritorio y en móvil). */
  const RowActions = ({ employee }) => (
    <div style={{ display: 'flex', gap: 6 }}>
      <IconButton
        icon="visibility"
        title={`Ver recibos de ${employee?.name}`}
        aria-label={`Ver recibos de ${employee?.name}`}
        onClick={() => handleViewEmployee(employee)}
      />
      <IconButton
        icon="key"
        title={`Crear acceso para ${employee?.name}`}
        aria-label={`Crear acceso para ${employee?.name}`}
        onClick={() => handleCreateAccess(employee)}
      />
      <IconButton
        icon="lock_reset"
        title={`Restablecer contraseña de ${employee?.name}`}
        aria-label={`Restablecer contraseña de ${employee?.name}`}
        onClick={() => handleResetPassword(employee)}
      />
    </div>
  );

  const Persona = ({ employee, sub }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
      <span
        className="a2-avatar"
        style={{
          width: 38, height: 38, fontSize: '.76rem', flexShrink: 0,
          background: 'var(--surface-2)', border: '1px solid var(--line-2)', color: 'var(--text)',
        }}
      >
        {initialsOf(employee?.name)}
      </span>
      <div style={{ minWidth: 0 }}>
        <div className="a2-tcell-strong a2-truncate" title={employee?.name}>{employee?.name}</div>
        <div className="a2-tcell-muted a2-truncate" title={sub ?? employee?.email}>{sub ?? employee?.email}</div>
      </div>
    </div>
  );

  // ── Sin empleados ──────────────────────────────────────────────────────────
  if (employeesData?.length === 0) {
    return (
      <div className="a2-card">
        <div style={{ padding: '18px 20px 14px', borderBottom: '1px solid var(--line)' }}>
          <h3 className="a2-card-title">Colaboradores</h3>
          <p className="a2-card-sub">Gestiona la información de empleados y sus nóminas</p>
        </div>
        <EmptyState
          icon="group_off"
          title="No hay empleados registrados"
          text={companyData?.company_name
            ? `Aún no hay colaboradores en ${companyData.company_name}. Se dan de alta solos al sincronizar los recibos con el SAT.`
            : 'Aún no hay colaboradores. Se dan de alta solos al sincronizar los recibos con el SAT.'}
          action={<Button icon="sync" onClick={goToPayrollProcessing}>Sincronizar con el SAT</Button>}
          className="a2-card"
          style={{ border: 'none' }}
        />
      </div>
    );
  }

  // ── Con empleados ──────────────────────────────────────────────────────────
  return (
    <div className="a2-card">
      {/* Encabezado + filtros */}
      <div style={{ padding: '18px 20px 14px', borderBottom: '1px solid var(--line)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10, marginBottom: 14 }}>
          <div style={{ minWidth: 0 }}>
            <h3 className="a2-card-title">Colaboradores</h3>
            <p className="a2-card-sub">
              {employeesData.length} en total
              {filteredAndSortedData.length !== employeesData.length
                ? ` · ${filteredAndSortedData.length} coinciden con el filtro`
                : ''}
            </p>
          </div>
          <Button variant="outline" size="sm" icon="sync" onClick={goToPayrollProcessing}>
            Sincronizar con el SAT
          </Button>
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          <SearchInput
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e?.target?.value); setCurrentPage(1); }}
            placeholder="Buscar por nombre, RFC, número o correo…"
            style={{ flex: 1, minWidth: 200, padding: '9px 12px', borderRadius: 9, background: 'var(--surface-2)' }}
            aria-label="Buscar colaborador"
          />
          <Select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e?.target?.value); setCurrentPage(1); }}
            aria-label="Filtrar por recibos del colaborador"
            style={{ width: 'auto', minWidth: 175, padding: '9px 12px', borderRadius: 9, fontSize: '.88rem' }}
          >
            {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </Select>
        </div>
      </div>

      {/* Sin coincidencias */}
      {filteredAndSortedData.length === 0 ? (
        <EmptyState
          icon="person_search"
          title="Sin coincidencias"
          text="Ajusta la búsqueda o el filtro de estado."
          className="a2-card"
          style={{ border: 'none' }}
        />
      ) : (
        <>
          {/* Escritorio: tabla con columnas ordenables */}
          <div className="a2-only-lg">
            <div style={{ overflowX: 'auto' }}>
              <div style={{ minWidth: GRID_MIN }}>
                <div className="a2-thead" style={{ gridTemplateColumns: GRID, gap: GRID_GAP }}>
                  <div>Acciones</div>
                  {COLUMNS.map((col) => (
                    <button
                      key={col.key}
                      type="button"
                      onClick={() => handleSort(col.key)}
                      title={`Ordenar por ${col.label}`}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none',
                        padding: 0, cursor: 'pointer', font: 'inherit', color: 'inherit', textAlign: 'left',
                      }}
                    >
                      {col.label}
                      <Sym
                        name={sortIcon(col.key)}
                        size={14}
                        style={{ color: sortConfig?.key === col.key ? 'var(--brand-2)' : 'var(--faint)' }}
                      />
                    </button>
                  ))}
                </div>

                {paginatedData?.map((employee) => (
                  <div key={employee?.id} className="a2-trow" style={{ gridTemplateColumns: GRID, gap: GRID_GAP }}>
                    <RowActions employee={employee} />
                    <Persona employee={employee} />
                    <div className="a2-tcell a2-mono a2-truncate">{employee?.rfc}</div>
                    <div className="a2-tcell a2-mono">#{employee?.employeeNumber}</div>
                    <div className="a2-tcell a2-mono">{employee?.receiptsCount ?? 0}</div>
                    <div className="a2-tcell">{formatDate(employee?.lastReceiptPeriod)}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Móvil: una tarjeta por colaborador */}
          <div className="a2-only-sm">
            {paginatedData?.map((employee) => (
              <div key={employee?.id} style={{ padding: '14px 16px', borderBottom: '1px solid var(--line)' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 12 }}>
                  <RowActions employee={employee} />
                  <Badge tone={employee?.receiptsCount > 0 ? 'ok' : 'neutral'}>
                    {employee?.receiptsCount ?? 0} recibo{employee?.receiptsCount === 1 ? '' : 's'}
                  </Badge>
                </div>

                <Persona
                  employee={employee}
                  sub={[employee?.position, employee?.department].filter(Boolean).join(' · ')}
                />

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 12 }}>
                  <div>
                    <div className="a2-tcell-muted">Último recibo</div>
                    <div className="a2-tcell">{formatDate(employee?.lastReceiptPeriod)}</div>
                  </div>
                  <div>
                    <div className="a2-tcell-muted">RFC</div>
                    <div className="a2-tcell a2-mono a2-truncate">{employee?.rfc}</div>
                  </div>
                  <div>
                    <div className="a2-tcell-muted">No. empleado</div>
                    <div className="a2-tcell a2-mono">#{employee?.employeeNumber}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Paginación */}
      {totalPages > 1 && (
        <div
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            flexWrap: 'wrap', gap: 10, padding: '14px 20px', borderTop: '1px solid var(--line)',
          }}
        >
          <span style={{ font: "500 .82rem 'Hanken Grotesk'", color: 'var(--muted)' }}>
            {((currentPage - 1) * itemsPerPage) + 1}–{Math.min(currentPage * itemsPerPage, filteredAndSortedData?.length)} de {filteredAndSortedData?.length}
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <IconButton
              icon="chevron_left"
              aria-label="Página anterior"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            />
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => i + 1).map((pageNum) => (
              <Button
                key={pageNum}
                size="sm"
                variant={currentPage === pageNum ? 'primary' : 'ghost'}
                onClick={() => setCurrentPage(pageNum)}
                aria-current={currentPage === pageNum ? 'page' : undefined}
                style={{ minWidth: 34, padding: '7px 10px' }}
              >
                {pageNum}
              </Button>
            ))}
            <IconButton
              icon="chevron_right"
              aria-label="Página siguiente"
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            />
          </div>
        </div>
      )}

      <CreateEmployeeAccessModal
        isOpen={createAccessModal.isOpen}
        onClose={handleCloseCreateAccessModal}
        employee={createAccessModal.employee}
      />
    </div>
  );
};

export default EmployeeDataTable;
